import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import JSPDF from "jspdf";
import JSZip from "jszip";
import autoTable, { RowInput, UserOptions } from "jspdf-autotable";

import {
	getExcelCurrencyFormat,
	createLogger,
	formatNumber,
} from "../../utils";
import { DateRange } from "../../types/DataRange";
import IResponseWithItems from "../../types/IResponse";
import { СurrencyDefinitions } from "../../types";
import {
	formatDate,
	formatDistance,
	formatMoney,
	formatTimeToMinutes,
	toExcelLocalDate,
} from "../../pages/Reports/pages/AccountingReports/utils/formatForFile";
import { CalendarNamespace } from "../../components/common";
import { Base, Language } from "..";

const logger = createLogger({ name: "CashlessOrdersReport" });

type OrderItem = CashlessOrdersReport.Model.Item["orders"][number];

const DATE_COLUMNS = new Set(["created", "when", "completed"]);

const getOrderDate = (columnId: string, order: OrderItem) => {
	if (columnId === "created") return order.createdAt;
	if (columnId === "when") return order.arrivalTime;
	if (columnId === "completed") return order.closedAt;
	return undefined;
};

/** Value of each table column as the table cell shows it (ModelTable/components) */
const getOrderText = (
	columnId: string,
	order: OrderItem,
	statusText: Record<string, string>,
): string => {
	switch (columnId) {
		case "order":
			return `${order.orderNumber ?? ""}`;
		case "name":
			return order.counterpartyName ?? "";
		case "card":
			return order.checkCard ?? "";
		case "phone":
			return order.phone ?? "";
		case "passenger":
			return order.fullName ?? "";
		case "from":
			return order.addressFrom ?? "";
		case "to":
			return order.addressTo ?? "";
		case "note":
			return order.executorNotes ?? "";
		case "orderNote":
			return order.orderNotes ?? "";
		case "callSign":
			return order.executorCallSign ?? "";
		case "licensePlate":
			return order.registrationNumber ?? "";
		case "model":
			return order.carType ?? "";
		case "color":
			return order.carColor ?? "";
		case "distanceKm":
			return `${formatDistance(order.distance)}`;
		case "executionTimeMin":
			return `${formatTimeToMinutes(order.executingTime)}`;
		case "idleTimeMin":
			return `${formatTimeToMinutes(order.delay)}`;
		case "amountUah":
			return formatNumber(order.price) ?? "";
		case "status":
			return order.status ? statusText.completed : statusText.pending;
		default: {
			if (DATE_COLUMNS.has(columnId)) {
				const date = getOrderDate(columnId, order);
				return date ? formatDate({ date }) : "";
			}
			return "";
		}
	}
};

const getOrderExcelValue = (
	columnId: string,
	order: OrderItem,
	statusText: Record<string, string>,
): string | number | Date | null => {
	if (DATE_COLUMNS.has(columnId)) {
		return toExcelLocalDate(getOrderDate(columnId, order));
	}
	if (columnId === "distanceKm") {
		return parseFloat(formatDistance(order.distance));
	}
	if (columnId === "amountUah") {
		return parseFloat(formatMoney(order.price));
	}
	if (columnId === "executionTimeMin" || columnId === "idleTimeMin") {
		return formatTimeToMinutes(
			columnId === "executionTimeMin" ? order.executingTime : order.delay,
		);
	}
	return getOrderText(columnId, order, statusText);
};

class CashlessOrdersReport extends Base {
	static fromResponse(data: any): CashlessOrdersReport.Model {
		return data || [];
	}

	public static async getCashlessOrdersReport(
		params: CashlessOrdersReport.SearchOptions,
	): Promise<CashlessOrdersReport.GetCashlessOrdersReportResponse | null> {
		try {
			logger.info(
				"[CashlessOrdersReport] getCashlessOrdersReport params",
				{
					params,
				},
			);
			if (!params.dateRange) return null;

			const response = await this.request((prpc) =>
				prpc.theirsModel.report.getCashlessOrdersReport(params),
			);

			logger.info(
				"[CashlessOrdersReport] getCashlessOrdersReport response",
				{
					response,
				},
			);
			if (!response) return null;

			return {
				...response,
				items: response?.items?.map(this.fromResponse),
			};
		} catch (error) {
			logger.error("[CashlessOrdersReport] Error", error);

			return null;
		}
	}

	private static parseDate(value: string | Date | undefined): number {
		try {
			if (!value) return 0;
			const date = value instanceof Date ? value : new Date(value);
			const time = date.getTime();
			return Number.isNaN(time) ? 0 : time;
		} catch {
			return 0;
		}
	}

	public static sortOrdersByCreatedAtDesc(
		data: CashlessOrdersReport.Model.Item[],
	): CashlessOrdersReport.Model.Item[] {
		return data.map((item) => ({
			...item,
			orders: [...item.orders].sort(
				(a, b) =>
					this.parseDate(b.createdAt) - this.parseDate(a.createdAt),
			),
		}));
	}

	public static exportPDF = async (
		data: CashlessOrdersReport.Model.Item[],
		options: {
			tableColumn: string[];
			columnStyles: UserOptions["columnStyles"];
			width: number;
			height: number;
			general: boolean;
			ColumnId: any;
			columnIds: any;
			textTranslations: Record<string, string>;
			headerTitle: string;
		},
	) => {
		if (!data?.length) return;
		const {
			tableColumn = [],
			width = 3500,
			height = 780,
			columnStyles,
			columnIds,
			textTranslations,
			headerTitle,
		} = options;

		const totalWidth = columnStyles
			? Object.values(columnStyles).reduce(
					(sum, { cellWidth }) => sum + +(cellWidth || 0),
					0,
			  ) +
			  Object.keys(columnStyles).length * (5.1 / 3.56) +
			  0.1 / 3
			: width;

		const realWidth = totalWidth || width;
		const doc = new JSPDF({
			orientation: "landscape",
			format: [realWidth, height],
		});

		let currentY = 10;
		let _dateFrom;
		let _dateTo;

		data.forEach(({ taxiService, orders, dateRange, counter }) => {
			doc.setFont("Roboto-Regular", "normal");

			const textCompany = taxiService?.company?.name ?? "";
			const textFill = taxiService?.name ? `, ${taxiService?.name}` : "";
			const textCounterparty = taxiService?.counterpartyName
				? taxiService?.counterpartyName
				: "";

			const dateFrom = formatDate({
				date: dateRange.from,
				isTitleFormat: true,
			});

			const dateTo = formatDate({
				date: dateRange.to,
				isTitleFormat: true,
			});

			if (!_dateFrom) _dateFrom = dateFrom;
			if (!_dateTo) _dateTo = dateTo;

			const pageWidth = doc.internal.pageSize.getWidth();
			const centerX = pageWidth / 2;

			const textArray = [
				`${textCompany} ${textFill}`,

				`${textCounterparty} ${textTranslations.forPeriod} ${textTranslations.from} ${dateFrom} ${textTranslations.to} ${dateTo}`,
			];

			textArray.forEach((text) => {
				const textWidth = doc.getTextWidth(text);

				const textX = centerX - textWidth / 2;

				doc.text(text, textX, currentY);

				currentY += 10;
			});

			const tableRows = orders.map(
				(order) =>
					(columnIds as string[]).map((columnId) => ({
						content: getOrderText(
							columnId,
							order,
							textTranslations,
						),
					})) as RowInput,
			);

			autoTable(doc, {
				head: [tableColumn],
				body: tableRows,
				startY: currentY,
				styles: {
					fontSize: 12,
					cellPadding: 5,
					lineWidth: 0.1,
					lineColor: [0, 0, 0],
					font: "Roboto-Regular",
					fontStyle: "normal",
				},
				columnStyles,
				headStyles: {
					fillColor: [41, 128, 185],
					textColor: [255, 255, 255],
					fontSize: 10,
				},
				margin: { top: 20 },
				tableLineWidth: 0,
				tableLineColor: [0, 0, 0],
				theme: "grid",
			});

			currentY = doc?.lastAutoTable?.finalY;

			const currency = orders?.[0]?.currency?.name || "";

			doc.setFont("Roboto-Regular", "normal");
			doc.text(
				[
					`${textTranslations.successful}: ${counter.success.count}    ${textTranslations.unsuccessful}: ${counter.filed.count}    ${textTranslations.totalOrders}: ${counter.orders.count}`,
					`${textTranslations.amount}, ${currency}: ${counter.success.amount}   ${textTranslations.amount}, ${currency}: ${counter.filed.amount}   ${textTranslations.total}, ${currency}: ${counter.orders.amount}`,
				],
				30,
				currentY + 20,
			);
			currentY += 50;
		});

		const namefile = `${headerTitle ?? ""} - ${_dateFrom ?? ""} - ${
			_dateTo ?? ""
		}.pdf`;

		doc.save(namefile);
	};

	public static exportExcel = async (
		_data: CashlessOrdersReport.Model.Item[],
		options: {
			tableColumn: string[];
			columnStyles: UserOptions["columnStyles"];
			width: number;
			height: number;
			general: boolean;
			ColumnId: any;
			columnIds: any;
			textTranslations: Record<string, string>;
			headerTitle: string;
			isZip: boolean;
			dateFrom: CalendarNamespace.Value;
			dateTo: CalendarNamespace.Value;
		},
	) => {
		if (!_data?.length) return;

		const {
			tableColumn = [],

			columnStyles,
			columnIds,
			textTranslations,
			headerTitle,
			isZip,
			dateFrom,
			dateTo,
		} = options;

		const _dateFrom = formatDate({
			date: dateFrom ?? new Date(),
			isTitleFormat: true,
		});
		const _dateTo = formatDate({
			date: dateTo ?? new Date(),
			isTitleFormat: true,
		});

		const namefile = `${headerTitle ?? ""} - ${_dateFrom ?? ""} - ${
			_dateTo ?? ""
		}`;

		const zip = new JSZip();
		const workbook = new ExcelJS.Workbook();

		const clearWorksheets = () => {
			workbook.worksheets.forEach((sheet) =>
				workbook.removeWorksheet(sheet.name),
			);
		};

		const createReport = async (
			data: CashlessOrdersReport.Model.Item[],
		) => {
			const worksheet = workbook.addWorksheet(namefile);

			tableColumn.forEach((colIndex, index) => {
				const cellWidth: number = +(
					columnStyles?.[index]?.cellWidth ?? 80
				);

				if (typeof cellWidth === "number" && cellWidth > 0) {
					worksheet.getColumn(index + 1).width = Math.round(
						cellWidth / 2.5,
					);
				} else {
					logger.warn(
						`2 Width for column ${colIndex} is undefined or invalid: cellWidth`,
						cellWidth,
					);
				}
			});

			data.forEach(({ taxiService, orders, dateRange, counter }) => {
				const textCompany = taxiService?.company?.name ?? "";
				const textFill = taxiService?.name
					? `, ${taxiService?.name}`
					: "";
				const textCounterparty = taxiService?.counterpartyName || "";

				const dateFrom = formatDate({
					date: dateRange.from,
					isTitleFormat: true,
				});
				const dateTo = formatDate({
					date: dateRange.to,
					isTitleFormat: true,
				});

				const addHeaderRow = (text: string) => {
					const headerRow = worksheet.addRow([text]);
					headerRow.font = {
						size: 10,
						name: "Arial",
					};
					headerRow.alignment = {
						horizontal: "left",
						vertical: "middle",
						wrapText: false,
					};

					headerRow.height = 30;

					worksheet.mergeCells(
						`A${headerRow.number}:F${headerRow.number}`,
					);

					headerRow.getCell(1).alignment = {
						horizontal: "center",
						vertical: "middle",
					};
				};

				const textheaderTable = [
					`${textCompany} ${textFill}`,
					`${textCounterparty} ${textTranslations.forPeriod} ${textTranslations.from} ${dateFrom} ${textTranslations.to} ${dateTo}`,
				];

				textheaderTable.forEach((text) => {
					addHeaderRow(text);
				});

				const titleRow = worksheet.addRow(tableColumn);
				titleRow.font = {
					name: "Calibri",
					size: 11,
					color: { argb: "ffffff" },
				};
				titleRow.alignment = {
					horizontal: "center",
					vertical: "middle",
					wrapText: true,
				};
				titleRow.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "4baac6" },
				};
				titleRow.border = {
					top: { style: "thin", color: { argb: "FF000000" } },
					bottom: { style: "thin", color: { argb: "FF000000" } },
				};

				const maxHeight = Math.min(
					3,
					Math.max(
						...tableColumn.map((cell) => {
							if (typeof cell === "string") {
								return Math.ceil(cell.length / 10);
							}
							return 1;
						}),
					),
				);

				titleRow.height = maxHeight * 13;

				orders.forEach((order, index) => {
					const ids = columnIds as string[];
					const row: (string | number | undefined | Date | null)[] =
						ids.map((columnId) =>
							getOrderExcelValue(
								columnId,
								order,
								textTranslations,
							),
						);

					function isOdd(num: number): boolean {
						return num % 2 !== 0;
					}

					const newRow = worksheet.addRow(row);
					newRow.alignment = {
						horizontal: "center",
						vertical: "middle",
						wrapText: true,
					};
					newRow.font = { name: "Arial", size: 10 };

					ids.forEach((columnId, columnIndex) => {
						const cell = newRow.getCell(columnIndex + 1);
						if (columnId === "distanceKm") cell.numFmt = "0.000";
						if (
							columnId === "executionTimeMin" ||
							columnId === "idleTimeMin"
						) {
							cell.numFmt = "0";
						}
						if (columnId === "amountUah") {
							const currency =
								orders?.[0]?.currency?.symbol || "₴";
							cell.numFmt = getExcelCurrencyFormat(currency);
							cell.alignment = { wrapText: true };
						}
						if (DATE_COLUMNS.has(columnId)) {
							cell.numFmt = "DD.MM.YYYY HH:mm:ss";
							cell.alignment = { wrapText: true };
						}
					});

					if (isOdd(index)) {
						newRow.fill = {
							type: "pattern",
							pattern: "solid",
							fgColor: { argb: "d9d9d9" },
						};
					}
					if (orders.length - 1 === index) {
						newRow.border = {
							bottom: {
								style: "thin",
								color: { argb: "FF000000" },
							},
						};
					}
					const maxHeight = Math.min(
						3,
						Math.max(
							...row.map((cell) => {
								if (typeof cell === "string") {
									return Math.ceil(cell.length / 10);
								}
								return 1;
							}),
						),
					);

					newRow.height = maxHeight * 13;

					newRow.alignment = {
						horizontal: "center",
						vertical: "middle",
						wrapText: true,
					};
				});

				const currency = orders?.[0]?.currency?.name || "";

				const footerOne = worksheet.addRow([
					textTranslations.successful,
					counter.success.count || 0,
					textTranslations.unsuccessful,
					counter.filed.count || 0,
					textTranslations.totalOrders,
					counter.orders.count || 0,
				]);

				const footerTwo = worksheet.addRow([
					`${textTranslations.amount}, ${currency}:`,
					counter.success.amount || 0,
					`${textTranslations.amount}, ${currency}:`,
					counter.filed.amount || 0,
					`${textTranslations.total}, ${currency}:`,
					counter.orders.amount || 0,
				]);

				footerOne.eachCell((cell) => {
					cell.border = {
						top: { style: "thin" },
						left: { style: "thin" },
						bottom: { style: "thin" },
						right: { style: "thin" },
					};
					cell.alignment = {
						horizontal: "center",
						vertical: "middle",
					};
					cell.font = { name: "Calibri", size: 11 };
					cell.fill = {
						type: "pattern",
						pattern: "solid",
						fgColor: { argb: "F9F9F9" },
					};
				});

				footerTwo.eachCell((cell) => {
					cell.border = {
						top: { style: "thin" },
						left: { style: "thin" },
						bottom: { style: "thin" },
						right: { style: "thin" },
					};
					cell.alignment = {
						horizontal: "center",
						vertical: "middle",
					};
					cell.font = { name: "Calibri", size: 11 };
					cell.fill = {
						type: "pattern",
						pattern: "solid",
						fgColor: { argb: "F9F9F9" },
					};
				});

				worksheet.addRow([]);
				worksheet.addRow([]);
			});

			const buffer = await workbook.xlsx.writeBuffer();

			if (isZip) {
				zip.file(
					`${data[0]?.taxiService?.counterpartyName ?? "Report"} (${
						data[0]?.taxiService?.edrpou ?? ""
					}).xlsx`,
					buffer,
				);
			} else {
				const blob = new Blob([buffer], {
					type: "application/octet-stream",
				});
				saveAs(blob, nameFileExcelOnly);
			}
		};

		const nameFileZip = `${namefile}.zip`;
		const nameFileExcelOnly = `${namefile}.xlsx`;

		if (isZip) {
			for (let index = 0; index < _data.length; index++) {
				clearWorksheets();
				await createReport([_data[index]]);
			}

			const zipContent = await zip.generateAsync({ type: "blob" });
			saveAs(zipContent, nameFileZip);
		} else {
			createReport(_data);
		}
	};
}

declare namespace CashlessOrdersReport {
	type Model = CashlessOrdersReport.Model.Item;

	interface GetCashlessOrdersReportResponse
		extends IResponseWithItems<Model> {}

	interface SearchOptions {
		successes?: boolean;
		general?: boolean;
		taxiServiceIds?: number[];
		counterpartyIds?: number[];
		cardIds?: number[];
		dateRange: DateRange;
		lang: Language;
	}

	namespace Model {
		interface AmountCount {
			count: number;
			amount: number;
		}
		interface Counter {
			success: AmountCount;
			filed: AmountCount;
			orders: AmountCount;
		}

		interface Order {
			id: number;
			orderNumber?: number;
			phone?: string;
			checkCard: string;
			fullName: string;

			addressFrom: string;
			addressTo: string;

			orderNotes: string;
			executorNotes: string;

			createdAt: string | Date;
			arrivalTime: number;
			closedAt: string | Date;

			executorCallSign: string;

			registrationNumber: string;
			carType: string;
			carColor: string;

			distance: number;

			delay: number;
			executingTime: number;

			price: number;
			currency: СurrencyDefinitions.Value;
			status: boolean;

			counterpartyName?: string;
		}

		interface Item {
			dateRange: {
				from: Date;
				to: Date;
			};
			taxiService: {
				id: number;
				name: string;
				company: {
					id: number;
					name: string;
				};
				counterpartyName?: string;
				edrpou?: string;
			};
			orders: Order[];
			counter: Counter;
		}
	}
}

export default CashlessOrdersReport;
